import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Route, Routes, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AuthProvider } from './auth/AuthProvider';
import { ProtectedRoute } from './auth/ProtectedRoute';
import { api } from './api/client';
import { useAuthStore } from './store/authStore';
import { LoginPage } from './pages/auth/LoginPage';
import { AtivarPage } from './pages/auth/AtivarPage';
import { SecurityCompliancePage } from './pages/auth/SecurityCompliancePage';
import { lazy, Suspense, useEffect, useState, useRef } from 'react';
import { installDuRuntimeAgent } from './utils/du-agent';
const ProfessorOnboardingPage = lazy(() => import('./pages/professor/ProfessorOnboardingPage'));
import { LandingPage } from './pages/public/LandingPage';
import { SobrePage } from './pages/public/SobrePage';
import { PoliticaPrivacidadePage } from './pages/public/PoliticaPrivacidadePage';
import { CancelarEmailPage } from './pages/public/CancelarEmailPage';
import { EcadPageModular } from './pages/public/ecad/pages/EcadPageModular';
import { EcadStudioPageModular } from './pages/public/ecad/pages/EcadStudioPageModular';
import { EcadRecorderPageModular } from './pages/public/ecad/pages/EcadRecorderPageModular';
import { EcadStudioPanelModular } from './pages/public/ecad/pages/EcadStudioPanelModular';
import { PwaUpdateWatcher } from './components/pwa/PwaUpdateWatcher';
import { ErrorBoundary } from './components/ui/ErrorBoundary';
import { ThemeLoader } from './components/layout/ThemeLoader';
import { EnvironmentBadge } from './components/shared/EnvironmentBadge';
import { MasterCustomizacaoPage } from './pages/master/MasterCustomizacaoPage';
import { Toaster } from 'sonner';
import { AcBootLoader } from './components/shared/AcBootLoader';
import { GlobalF4Handler } from './components/GlobalF4Handler';
import { LabContextProvider, LabExecutionOverlay } from './lab';


// Layouts
import { AdminLayout } from './components/layout/AdminLayout';
import { MasterLayout } from './components/layout/MasterLayout';
import { MunicipalLayout } from './components/layout/MunicipalLayout';
import { EstadualLayout } from './components/layout/EstadualLayout';
import { RedeLayout } from './components/layout/RedeLayout';
import { ProfessorLayout } from './components/layout/ProfessorLayout';
import { EquipeLayout } from './components/layout/EquipeLayout';
import { ResponsavelLayout } from './components/layout/ResponsavelLayout';
import { AlunoLayout } from './components/layout/AlunoLayout';
import { DashboardBriefing } from './components/briefing/DashboardBriefing';

// Admin — CRUDs + Operacional + Gestão
import { Dashboard } from './pages/admin/Dashboard';
import { SetupPage } from './pages/admin/SetupPage';
import { DashboardMaster } from './pages/master/DashboardMaster';
import { MasterBillingPage } from './pages/master/MasterBillingPage';
import { MasterConfigPage } from './pages/master/MasterConfigPage';
import { MasterBackupsPage } from './pages/master/MasterBackupsPage';
import { MasterVersionsPage } from './pages/master/MasterVersionsPage';
import { MasterHealthPage } from './pages/master/MasterHealthPage';
import { LoginAcessosPage } from './pages/master/LoginAcessosPage';
import { MasterMapaPage } from './pages/master/MasterMapaPage';
import { MasterFinanceiroPage } from './pages/master/MasterFinanceiroPage';
import { MasterIndicesPage } from './pages/master/MasterIndicesPage';
import { MasterCrescimentoPage } from './pages/master/MasterCrescimentoPage';
import { MasterAssinaturasPage } from './pages/master/MasterAssinaturasPage';
import { MasterPlanosPage } from './pages/master/MasterPlanosPage';
import { MasterFeedbackPage } from './pages/master/MasterFeedbackPage';
import { MasterAgentesIaPage } from './pages/master/MasterAgentesIaPage';
import { MasterRfidDashboard } from './pages/master/MasterRfidDashboard';
import { MasterDispositivosPage } from './pages/master/MasterDispositivosPage';
import { MasterCredenciaisPage } from './pages/master/MasterCredenciaisPage';
import { ComunicadosMasterPage } from './pages/master/ComunicadosMasterPage';
import { MasterLabTestesPage } from './pages/master/MasterLabTestesPage';
import { SuporteChamadosPage } from './pages/suporte/SuporteChamadosPage';

import { OportunidadesPage } from './pages/comercial/OportunidadesPage';
import { OportunidadeDetalhePage } from './pages/comercial/OportunidadeDetalhePage';
import { ContratosPage } from './pages/comercial/ContratosPage';
import { ModuloEmConstrucao } from './components/shared/ModuloEmConstrucao';
import { TvGradeManagementPage } from './pages/admin/TvGradeManagementPage';
import { EadCentroConhecimento } from './pages/shared/EadCentroConhecimento';
import { EscolaDetalhePage } from './pages/gestao/EscolaDetalhe';
import { MunicipioDetalhePage } from './pages/gestao/MunicipioDetalhe';
import { EstadoDetalhePage } from './pages/gestao/EstadoDetalhe';
import { SimulationSetupPage } from './features/developer-cockpit/pages/SimulationSetupPage';
import { DevConfigPage } from './features/developer-cockpit/pages/DevConfigPage';
import { SimStudioPage } from './features/simulation-studio/pages/SimStudioPage';
import { SimOnboardingPage } from './features/simulation-studio/pages/SimOnboardingPage';
import { DemoPage } from './pages/public/DemoPage';
import { DownloadPage } from './pages/public/DownloadPage';
import { PublicTvPage } from './pages/public/PublicTvPage';
import { ExemplosMasterPage } from './pages/public/exemplos/ExemplosMasterPage';
import { PoliticoLandingPage } from './pages/public/exemplos/PoliticoLandingPage';
import { EscolaPublicaLandingPage } from './pages/public/exemplos/EscolaPublicaLandingPage';
import { EnsinoTecnicoLandingPage } from './pages/public/exemplos/EnsinoTecnicoLandingPage';
import { InstallPage } from './pages/public/InstallPage';
import { PublicDashboardPage } from './pages/public/PublicDashboardPage';
import { AuditReportPage } from './features/telemetry/AuditReportPage';
import { ValidarDocumento } from './pages/public/ValidarDocumento';
import { DashboardRede } from './pages/admin/DashboardRede';
import { DashboardEstado } from './pages/estadual/DashboardEstado';
import { UsuariosEstadual } from './pages/estadual/UsuariosEstadual';
import { UsuariosRede } from './pages/rede/UsuariosRede';
import { DashboardMunicipio } from './pages/municipal/DashboardMunicipio';
import { ComunicadosMunicipal } from './pages/municipal/ComunicadosMunicipal';
import { ConfiguracoesMunicipal } from './pages/municipal/ConfiguracoesMunicipal';
import { MidiasMunicipal } from './pages/municipal/MidiasMunicipal';
import { chatPremiumRoutes } from './components/chat/production/chatRoutes';
import { ChatPopoutPage } from './pages/chat/ChatPopoutPage';
import { EscolasMunicipal } from './pages/municipal/EscolasMunicipal';
import { UsuariosMunicipal } from './pages/municipal/UsuariosMunicipal';
import { RelatoriosMunicipal } from './pages/municipal/RelatoriosMunicipal';
import { MapaMunicipal } from './pages/municipal/MapaMunicipal';
import TvGradeMunicipal from './pages/municipal/TvGradeMunicipal';
import { DashboardNOC } from './pages/noc/DashboardNOC';
import { DashboardTurma } from './pages/admin/DashboardTurma';
import { DashboardEscolaConsolidado } from './pages/admin/DashboardEscolaConsolidado';
import { TurnosPage } from './pages/admin/Turnos';
import { MateriasPage } from './pages/admin/Materias';
import { EquipePage } from './pages/admin/Equipe';
import { DispositivosPage } from './pages/admin/Dispositivos';
import { TurmasPage } from './pages/admin/Turmas';
import { TurmaDetalhePage } from './pages/admin/TurmaDetalhe';
import { AlunosPage } from './pages/admin/Alunos';
import { AlunoFichaPage } from './pages/admin/AlunoFicha';
import { ProfessoresPage } from './pages/admin/Professores';
import { ProfessorDetalhePage } from './pages/admin/ProfessorDetalhe';
import { ResponsaveisPage } from './pages/admin/Responsaveis';
import { ChamadaPage } from './pages/admin/Chamada';
import { ChamadaGestaoPage } from './pages/admin/ChamadaGestao';
import { ComunicadosPage } from './pages/admin/Comunicados';
import { ComunicadosEscolaPage } from './pages/admin/ComunicadosEscola';
import { MidiasEscolaPage } from './pages/admin/MidiasEscola';
import ComunicadosStatsEscola from './pages/admin/ComunicadosStatsEscola';
import ComunicadosMuralEscola from './pages/admin/ComunicadosMuralEscola';
import TvGradeEscola from './pages/admin/TvGradeEscola';
import ComunicadosStatsMunicipal from './pages/municipal/ComunicadosStatsMunicipal';
import ComunicadosMuralMunicipal from './pages/municipal/ComunicadosMuralMunicipal';
import { AcessoPage } from './pages/admin/Acesso';
import { AprovacoesPage } from './pages/admin/AprovacoesPage';
import { RedesPage } from './pages/gestao/Redes';
import { EstadosPage } from './pages/gestao/Estados';
import { MunicipiosPage } from './pages/gestao/Municipios';
import { EscolasPage } from './pages/gestao/Escolas';
import { UsuariosPage } from './pages/gestao/Usuarios';
import { AtivacoesPage } from './pages/admin/AtivacoesPage';
import { PainelSegurancaPage } from './pages/seguranca/PainelSegurancaPage';
import { ChatDesktop } from './pages/admin/ChatDesktop';
import { JustificativasGestao } from './pages/admin/JustificativasGestao';
import { CoberturaGradePage } from './pages/admin/CoberturaGrade';

// Dashboards por Perfil
import { ProfessorDashboard } from './pages/professor/ProfessorDashboard';
import { MinhasTurmasPage } from './pages/professor/MinhasTurmas';
import { AvaliacoesPage } from './pages/professor/AvaliacoesPage';
import { ObservacoesPage } from './pages/professor/ObservacoesPage';
import { RelatoriosProfessorPage } from './pages/professor/RelatoriosProfessorPage';
import { DiarioClassePage } from './pages/professor/DiarioClassePage';
import { AvaliacaoDetalhePage } from './pages/professor/AvaliacaoDetalhePage';
import { ProfessorComunicadosPage, ProfessorAlunosPage, ProfessorPerfilPage, ProfessorConfiguracoesPage } from './pages/professor/ProfessorSharedPages';
import { ProfessorAlunoFichaPage } from './pages/professor/ProfessorAlunoFicha';
import { ProfessorTurmaRedirectPage } from './pages/professor/ProfessorTurmaRedirect';
import { EquipeDashboard } from './pages/equipe/EquipeDashboard';
import { IncidentesPage } from './pages/equipe/IncidentesPage';
import { RelatoriosPage } from './pages/equipe/RelatoriosPage';
import { ResponsavelDashboard } from './pages/responsavel/ResponsavelDashboard';
import { MeusFilhos as MeusFilhosPage } from './pages/responsavel/MeusFilhos';
import { Frequencia as FrequenciaPage } from './pages/responsavel/Frequencia';
import { ResponsavelComunicados as ResponsavelComunicadosPage } from './pages/responsavel/ResponsavelComunicados';
import { Notificacoes as NotificacoesPage } from './pages/responsavel/Notificacoes';
import { Justificativas as JustificativasPage } from './pages/responsavel/Justificativas';
import { Desempenho as DesempenhoPage } from './pages/responsavel/Desempenho';
import { ResponsavelObservacoes as ResponsavelObservacoesPage } from './pages/responsavel/ResponsavelObservacoes';
import { ResponsavelEscola as ResponsavelEscolaPage } from './pages/responsavel/ResponsavelEscola';
import { AlunoDashboard } from './pages/aluno/AlunoDashboard';
import { MinhaGradePage } from './pages/aluno/MinhaGrade';
import { FrequenciaAlunoPage } from './pages/aluno/FrequenciaAluno';

// Shared
import { PerfilPage } from './pages/shared/PerfilPage';
import { MeusAlertasPage } from './pages/shared/MeusAlertasPage';
import { ConfiguracoesPage } from './pages/shared/ConfiguracoesPage';
import { AgendaConsolidada } from './pages/shared/AgendaConsolidada';
import { ChatInterno } from './pages/shared/ChatInterno';

// Security & Onboarding (Fases A-E + Primeiro Login)
import SecurityBondingPage from './pages/security/SecurityBondingPage';
import SecuritySuccessPage from './pages/security/SecuritySuccessPage';
import { ApprovePage } from './pages/security/ApprovePage';
import { RemoteCaptureMobilePage } from './pages/security/RemoteCaptureMobilePage';


// Security — Inactivity Lock
import { InactivityProvider } from './components/security/InactivityProvider';
import { PwaBiometricLockProvider } from './components/security/PwaBiometricLockProvider';
import { PwaStartupResolver } from './components/security/PwaStartupResolver';
import { PushNotificationManager } from './components/security/PushNotificationManager';
import { InAppNotificationWatcher } from './components/notifications/InAppNotificationWatcher';
import { PwaActivationRedirector } from './components/security/PwaActivationRedirector';

// Wizards de Onboarding por Perfil
import { DiretorIntroWizard } from './pages/onboarding/escola/diretor/DiretorIntroWizard';
const SecurityOnboardingPage = lazy(() => import('./pages/onboarding/SecurityOnboardingPage'));

// Mobile PWA
import { MobileLayout } from './layouts/MobileLayout';
import { MobileHome } from './pages/mobile/MobileHome';
import { FilhosTimeline } from './pages/mobile/FilhosTimeline';
import { AlunoGrade } from './pages/mobile/AlunoGrade';
import { Comunicados as MobileComunicados } from './pages/mobile/Comunicados';
import { MobilePerfil } from './pages/mobile/MobilePerfil';
import { Conversas } from './pages/mobile/Conversas';
import { ConversaDetalhe } from './pages/mobile/ConversaDetalhe';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
  },
});

const ModulePlaceholder = ({ name }: { name: string }) => (
  <div className="admin-page">
    <div style={{ textAlign: 'center', padding: 60, color: '#9ca3af' }}>
      <h2 style={{ fontSize: '1.2rem', fontWeight: 600, marginBottom: 8 }}>{name}</h2>
      <p>Módulo em construção</p>
    </div>
  </div>
);

function ExposeSelectors() {
  const location = useLocation();
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type === 'android_navigation') {
        const action = event.data.action;
        console.log('[AndroidNavigation] Comando recebido no PWA:', action);
        if (action === 'back') {
          window.history.back();
        } else if (action === 'home' && user) {
          const perfilRoutes: Record<string, string> = {
            ADMIN: '/master/dashboard',
            ESCOLA: '/admin/dashboard',
            PROFESSOR: '/professor/dashboard',
            ALUNO: '/aluno/dashboard',
            RESPONSAVEL: '/responsavel/dashboard',
            MUNICIPAL: '/municipal/dashboard',
            ESTADUAL: '/estadual/dashboard',
            REDE: '/rede/dashboard',
          };
          const dest = perfilRoutes[user.perfil] || '/login';
          navigate(dest);
        }
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [navigate, user]);

  useEffect(() => {
    fetch('/help_manifest.json')
      .then(res => {
        if (!res.ok) throw new Error();
        return res.json();
      })
      .then(manifest => {
        if (!manifest || !manifest.routes) return;
        const pathname = location.pathname;
        let routeEntry = manifest.routes[pathname];

        // Busca por prefixo se não houver exata
        if (!routeEntry) {
          const segments = pathname.split('/');
          while (segments.length > 1) {
            segments.pop();
            const prefix = segments.join('/');
            if (manifest.routes[prefix]) {
              routeEntry = manifest.routes[prefix];
              break;
            }
          }
        }

        if (routeEntry && routeEntry.selectors) {
          (window as any).__ac_selectors = routeEntry.selectors;
        } else {
          (window as any).__ac_selectors = {};
        }
      })
      .catch(() => {
        (window as any).__ac_selectors = {};
      });
  }, [location.pathname]);

  return null;
}

function isDuRuntimeSecuritySafe(): boolean {
  try {
    return (
      import.meta.env.VITE_DU_SECURITY_SAFE === '1' ||
      localStorage.getItem('DU_SECURITY_SAFE') === '1'
    );
  } catch {
    return import.meta.env.VITE_DU_SECURITY_SAFE === '1';
  }
}

interface ServiceStatus {
  name: string;
  label: string;
  status: string;
  statusLabel?: string;
  tone?: string;
  portCheck?: { ok: boolean };
}

function DevStatusGate({ children }: { children: React.ReactNode }) {
  const isLocalDev =
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1' ||
    window.location.hostname.includes('.local');

  const [status, setStatus] = useState<{
    loading: boolean;
    allReady: boolean;
    services: ServiceStatus[];
  }>({
    loading: true,
    allReady: false,
    services: [],
  });

  const hasSupervisorRef = useRef<boolean | null>(null);

  useEffect(() => {
    if (!isLocalDev) {
      setStatus({ loading: false, allReady: true, services: [] });
      return;
    }

    if (status.allReady) {
      return;
    }

    let active = true;
    let timerId: any;

    const checkStatus = async () => {
      // Se o Supervisor já foi detectado como offline, faz apenas a checagem direta do Backend
      if (hasSupervisorRef.current === false) {
        try {
          const backendRes = await fetch('/health/ready');
          if (backendRes.ok) {
            const backendData = await backendRes.json();
            if (backendData.status === 'ready' && active) {
              setStatus({
                loading: false,
                allReady: true,
                services: [
                  { name: 'dashboard', label: 'Painel local (Supervisor)', status: 'offline', tone: 'warn' },
                  { name: 'backend', label: 'Backend', status: 'running', tone: 'ok', portCheck: { ok: true } },
                  { name: 'frontend', label: 'Frontend', status: 'running', tone: 'ok', portCheck: { ok: true } },
                ],
              });
            }
          }
        } catch (e) {
          // Ignora
        }
        return;
      }

      try {
        const res = await fetch('/api/cockpit');
        if (!res.ok) throw new Error('Offline');
        const data = await res.json();

        if (!active) return;

        // Se o proxy retornou status offline silencioso, marca que o supervisor não existe
        if (data.status === 'offline') {
          hasSupervisorRef.current = false;

          // Executa a checagem direta do backend imediatamente para não perder tempo
          try {
            const backendRes = await fetch('/health/ready');
            if (backendRes.ok) {
              const backendData = await backendRes.json();
              if (backendData.status === 'ready') {
                setStatus({
                  loading: false,
                  allReady: true,
                  services: [
                    { name: 'dashboard', label: 'Painel local (Supervisor)', status: 'offline', tone: 'warn' },
                    { name: 'backend', label: 'Backend', status: 'running', tone: 'ok', portCheck: { ok: true } },
                    { name: 'frontend', label: 'Frontend', status: 'running', tone: 'ok', portCheck: { ok: true } },
                  ],
                });
                return;
              }
            }
          } catch (e) {
            // Ignora
          }

          setStatus({
            loading: false,
            allReady: false,
            services: [
              { name: 'dashboard', label: 'Painel local (Supervisor)', status: 'offline', tone: 'error' },
              { name: 'backend', label: 'Backend', status: 'offline', tone: 'error' },
              { name: 'frontend', label: 'Frontend', status: 'running', tone: 'ok' },
            ],
          });
          return;
        }

        hasSupervisorRef.current = true;
        const backend = data.services?.find((s: any) => s.name === 'backend');
        const backendReady = backend && (backend.status === 'running' || backend.status === 'external') && backend.portCheck?.ok;

        if (!backendReady) {
          try {
            const backendRes = await fetch('/health/ready');
            if (backendRes.ok) {
              const backendData = await backendRes.json();
              if (backendData.status === 'ready') {
                setStatus({
                  loading: false,
                  allReady: true,
                  services: [
                    { name: 'dashboard', label: 'Painel local (Supervisor)', status: 'offline', tone: 'warn' },
                    { name: 'backend', label: 'Backend', status: 'running', tone: 'ok', portCheck: { ok: true } },
                    { name: 'frontend', label: 'Frontend', status: 'running', tone: 'ok', portCheck: { ok: true } },
                  ],
                });
                return;
              }
            }
          } catch (e) {
            // Ignora
          }
        }

        setStatus({
          loading: false,
          allReady: !!backendReady,
          services: data.services || [],
        });
      } catch (err) {
        if (!active) return;

        // Fallback: se o Supervisor está offline, tenta checar o Backend diretamente
        try {
          const backendRes = await fetch('/health/ready');
          if (backendRes.ok) {
            const backendData = await backendRes.json();
            if (backendData.status === 'ready') {
              setStatus({
                loading: false,
                allReady: true,
                services: [
                  { name: 'dashboard', label: 'Painel local (Supervisor)', status: 'offline', tone: 'warn' },
                  { name: 'backend', label: 'Backend', status: 'running', tone: 'ok', portCheck: { ok: true } },
                  { name: 'frontend', label: 'Frontend', status: 'running', tone: 'ok', portCheck: { ok: true } },
                ],
              });
              return;
            }
          }
        } catch (e) {
          // Ignora e prossegue para marcar como offline
        }

        setStatus({
          loading: false,
          allReady: false,
          services: [
            { name: 'dashboard', label: 'Painel local (Supervisor)', status: 'offline', tone: 'error' },
            { name: 'backend', label: 'Backend', status: 'offline', tone: 'error' },
            { name: 'frontend', label: 'Frontend', status: 'running', tone: 'ok' },
          ],
        });
      }
    };

    checkStatus();
    timerId = setInterval(checkStatus, 3000);

    return () => {
      active = false;
      clearInterval(timerId);
    };
  }, [isLocalDev, status.allReady]);

  if (false && isLocalDev && !status.allReady && !status.loading) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        backgroundColor: '#020617',
        color: '#f8fafc',
        fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        padding: '2rem',
        textAlign: 'center'
      }}>
        <div style={{
          maxWidth: '480px',
          width: '100%',
          backgroundColor: '#0f172a',
          borderRadius: '16px',
          padding: '2.5rem',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.5), 0 10px 10px -5px rgba(0, 0, 0, 0.4)',
          border: '1px solid #1e293b'
        }}>
          {/* Header */}
          <div style={{ marginBottom: '2.25rem' }}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0.375rem 0.75rem',
              borderRadius: '9999px',
              backgroundColor: '#e11d4815',
              border: '1px solid #e11d4830',
              marginBottom: '1rem'
            }}>
              <span style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                backgroundColor: '#ef4444',
                marginRight: '8px',
                display: 'inline-block'
              }} />
              <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#f43f5e', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Ambiente Local - Aguardando Stack
              </span>
            </div>

            <h1 style={{ fontSize: '1.8rem', fontWeight: 700, margin: '0.5rem 0 0.75rem', color: '#f8fafc', letterSpacing: '-0.02em' }}>
              Carregando Serviços...
            </h1>
            <p style={{ color: '#94a3b8', fontSize: '0.9rem', lineHeight: '1.5', margin: 0 }}>
              O backend ou banco de dados ainda estão iniciando. A aplicação será liberada automaticamente assim que o backend estiver pronto.
            </p>
          </div>

          {/* Service status list */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', textAlign: 'left', marginBottom: '2.25rem' }}>
            {status.services.map((svc) => {
              if (svc.name === 'dashboard' || svc.name === 'docker') return null;

              let statusLabel = svc.statusLabel || svc.status;
              let statusColor = '#94a3b8';
              let badgeBg = '#1e293b';

              const isHealthy = (svc.status === 'running' || svc.status === 'external') && svc.portCheck?.ok;
              if (isHealthy) {
                statusColor = '#10b981';
                badgeBg = '#065f4620';
                statusLabel = 'Pronto';
              } else if (svc.status === 'running') {
                statusColor = '#f59e0b';
                badgeBg = '#78350f20';
                statusLabel = 'Iniciando...';
              } else if (svc.tone === 'error' || svc.status === 'failed' || svc.status === 'offline') {
                statusColor = '#ef4444';
                badgeBg = '#991b1b20';
              }

              return (
                <div key={svc.name} style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '0.875rem 1.25rem',
                  backgroundColor: '#1e293b40',
                  borderRadius: '10px',
                  border: '1px solid #33415550'
                }}>
                  <span style={{ fontWeight: 500, color: '#e2e8f0', fontSize: '0.95rem' }}>{svc.label}</span>
                  <span style={{
                    fontWeight: 600,
                    color: statusColor,
                    fontSize: '0.8rem',
                    backgroundColor: badgeBg,
                    padding: '0.25rem 0.625rem',
                    borderRadius: '6px',
                    border: `1px solid ${statusColor}20`
                  }}>
                    {statusLabel}
                  </span>
                </div>
              );
            })}
          </div>

          {/* Footer note */}
          <div style={{ fontSize: '0.8rem', color: '#64748b', borderTop: '1px solid #1e293b', paddingTop: '1.25rem', lineHeight: '1.6' }}>
            Acompanhe o carregamento detalhado no terminal executando:<br />
            <code style={{ display: 'block', padding: '0.625rem', background: '#020617', borderRadius: '6px', marginTop: '0.625rem', color: '#38bdf8', fontFamily: 'monospace', border: '1px solid #1e293b' }}>
              node run_server.js guardian
            </code>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}

function App() {
  useEffect(() => {
    // Redirecionamento automático de HTTP para HTTPS em produção/ngrok (evita falha de sensores no celular)
    // Ignoramos em ambiente de desenvolvimento (Vite dev server) ou se for host local/privado
    if (import.meta.env.DEV) return;

    const hostname = window.location.hostname;
    const isLocalHost =
      hostname === 'localhost' ||
      hostname === '127.0.0.1' ||
      hostname.endsWith('.local') ||
      hostname.startsWith('192.168.') ||
      hostname.startsWith('10.') ||
      /^(172\.(1[6-9]|2[0-9]|3[0-1]))\./.test(hostname); // Faixa IP privado 172.16.0.0 - 172.31.255.255

    if (window.location.protocol === 'http:' && !isLocalHost) {
      window.location.replace(window.location.href.replace('http:', 'https:'));
    }
  }, []);

  useEffect(() => {
    const handleKeyDown = async (e: KeyboardEvent) => {
      if (e.ctrlKey && e.key === 'F9') {
        const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
        if (!isLocal) return;

        // Se o usuário já estiver logado (com token ativo) e não estiver na tela de login,
        // deixa propagar para que o BaseLayout ou outros componentes tratem o atalho.
        const token = sessionStorage.getItem('ac_token') || localStorage.getItem('ac_token');
        const isLoginPath = window.location.pathname === '/login' || window.location.pathname === '/';
        if (token && !isLoginPath) {
          return;
        }

        e.preventDefault();
        e.stopPropagation();

        console.log('[AutoLogin] Executando Ctrl+F9 de login automático local...');
        try {
          const { data } = await api.post('/auth/login', {
            email: 'admin@alunoconectado.com.br',
            senha: 'Admin@2026'
          });

          const userData = data.user || data.userData || {
            id: data.id,
            nome: data.nome || 'Administrador',
            email: 'admin@alunoconectado.com.br',
            perfil: 'MASTER',
            ativo: true
          };

          // Ativar bypasses locais
          localStorage.setItem('ac_dev_bypass', 'true');
          document.cookie = 'ac_dev_bypass=true; path=/; max-age=31536000';

          useAuthStore.getState().login(data.access_token, userData);

          // Redirecionar para o dashboard principal
          window.location.href = '/master/dashboard';
        } catch (err) {
          console.error('[AutoLogin] Erro no login automático:', err);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, []);

  useEffect(() => {
    const enabled =
      import.meta.env.DEV ||
      import.meta.env.VITE_DU_AGENT === '1';

    if (!enabled) return;

    const agent = installDuRuntimeAgent({
      securitySafe: isDuRuntimeSecuritySafe(),
    });
    agent.start();

    return () => {
      agent.stop();
    };
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BrowserRouter>
          <PwaActivationRedirector />
          <DevStatusGate>
            <ThemeLoader />
            <ExposeSelectors />
            <PwaStartupResolver />
            <PushNotificationManager />
            <EnvironmentBadge />

            <LabContextProvider manifest={null} enabled={import.meta.env.DEV || import.meta.env.VITE_LAB_MODE === '1'}>
            <InactivityProvider>
              <PwaBiometricLockProvider>
                <InAppNotificationWatcher>
                <Routes>
                  <Route path="/" element={<LandingPage />} />
                  <Route path="/sobre" element={<SobrePage />} />
                  <Route path="/politica-de-privacidade" element={<PoliticaPrivacidadePage />} />
                  <Route path="/cancelar-email" element={<CancelarEmailPage />} />
                  <Route path="/exemplos" element={<ExemplosMasterPage />} />
                  <Route path="/exemplos/landing/politico" element={<PoliticoLandingPage />} />
                  <Route path="/exemplos/landing/escola-publica" element={<EscolaPublicaLandingPage />} />
                  <Route path="/exemplos/landing/ensino-tecnico" element={<EnsinoTecnicoLandingPage />} />
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/security-compliance" element={<SecurityCompliancePage />} />
                  <Route path="/ativar" element={<AtivarPage />} />
                  <Route path="/ativar/:token/:code?" element={<AtivarPage />} />
                  <Route path="/convite" element={<Navigate to="/ativar" replace />} />
                  <Route path="/simulador" element={<SimulationSetupPage />} />
                  {/* /dev-config — DesktopWindowManager isolado, rota segura para restauração do /dev */}
                  <Route path="/dev-config" element={<ProtectedRoute allowedPerfis={[]}><DevConfigPage /></ProtectedRoute>} />
                  <Route path="/sim" element={<ProtectedRoute allowedPerfis={[]}><SimStudioPage /></ProtectedRoute>} />
                  <Route path="/sim2" element={<SimOnboardingPage />} />
                  <Route path="/demo" element={<DemoPage />} />
                  <Route path="/download" element={<DownloadPage />} />
                  <Route path="/chat-popout" element={<ProtectedRoute allowedPerfis={[]}><ChatPopoutPage /></ProtectedRoute>} />
                  <Route path="/tv" element={<PublicTvPage />} />
                  <Route path="/ecad" element={<EcadPageModular />} />
                  <Route path="/ecad/:modulo" element={<EcadPageModular />} />
                  <Route path="/ecad-legacy" element={<EcadPageModular />} />
                  <Route path="/ecad-legacy/:modulo" element={<EcadPageModular />} />
                  <Route path="/ecad-modular" element={<EcadPageModular />} />
                  <Route path="/ecad-modular/:modulo" element={<EcadPageModular />} />
                  <Route path="/ecadstudio" element={<ProtectedRoute allowedPerfis={['ADMIN']}><ErrorBoundary><EcadStudioPageModular /></ErrorBoundary></ProtectedRoute>} />
                  <Route path="/ecadstudio-legacy" element={<ProtectedRoute allowedPerfis={['ADMIN']}><ErrorBoundary><EcadStudioPageModular /></ErrorBoundary></ProtectedRoute>} />
                  <Route path="/ecadstudio-modular" element={<ProtectedRoute allowedPerfis={['ADMIN']}><ErrorBoundary><EcadStudioPanelModular perfis={[]} onRefreshCatalog={() => { }} onBackToPlayer={() => { }} /></ErrorBoundary></ProtectedRoute>} />
                  <Route path="/ecadstudio-modular-crud" element={<ProtectedRoute allowedPerfis={['ADMIN']}><ErrorBoundary><EcadStudioPageModular /></ErrorBoundary></ProtectedRoute>} />
                  <Route path="/master/recorder" element={<ProtectedRoute allowedPerfis={['ADMIN']}><ErrorBoundary><EcadRecorderPageModular /></ErrorBoundary></ProtectedRoute>} />
                  <Route path="/master/recorder-legacy" element={<ProtectedRoute allowedPerfis={['ADMIN']}><ErrorBoundary><EcadRecorderPageModular /></ErrorBoundary></ProtectedRoute>} />
                  <Route path="/master/recorder-modular" element={<ProtectedRoute allowedPerfis={['ADMIN']}><ErrorBoundary><EcadRecorderPageModular /></ErrorBoundary></ProtectedRoute>} />
                  <Route path="/install" element={<InstallPage />} />
                  <Route path="/audit-report" element={<AuditReportPage />} />

                  {/* ═══ ROTAS PÚBLICAS DE SEGURANÇA E VALIDAÇÃO DE DOCUMENTOS (sem JWT) ═══ */}
                  <Route path="/validar" element={<ValidarDocumento />} />
                  <Route path="/validar/:codigo" element={<ValidarDocumento />} />
                  <Route path="/s/:sessionId" element={<SecurityBondingPage />} />
                  <Route path="/security/bonding" element={<SecurityBondingPage />} />
                  <Route path="/security/success" element={<SecuritySuccessPage />} />
                  <Route path="/security/approve" element={<ApprovePage />} />
                  <Route path="/security/approve/:id" element={<ApprovePage />} />
                  <Route path="/security/captura-remota/:id" element={<RemoteCaptureMobilePage />} />



                  {/* === WIZARDS DE INTRODUCAO POR PERFIL (com JWT) === */}
                  <Route path="/onboarding/escola/diretor" element={<ProtectedRoute allowedPerfis={['ESCOLA']}><ErrorBoundary><DiretorIntroWizard /></ErrorBoundary></ProtectedRoute>} />
                  <Route path="/onboarding/professor" element={<ProtectedRoute allowedPerfis={['PROFESSOR']}><Suspense fallback={<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100dvh', background: '#020617', color: '#94a3b8' }}>Carregando...</div>}><ProfessorOnboardingPage /></Suspense></ProtectedRoute>} />
                  <Route path="/onboarding/security" element={<ProtectedRoute skipOnboarding><Suspense fallback={<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100dvh', background: '#020617', color: '#94a3b8' }}>Carregando...</div>}><SecurityOnboardingPage /></Suspense></ProtectedRoute>} />

                  {/* ═══ MASTER LAB TESTES (Standalone Fullscreen — sem Sidebar e sem Top Header) ═══ */}
                  <Route path="/master/laboratorio-testes" element={<ProtectedRoute allowedPerfis={['ADMIN']}><ErrorBoundary><MasterLabTestesPage /></ErrorBoundary></ProtectedRoute>} />

                  {/* ═══ MASTER (Admin da Empresa) ═══ */}
                  <Route path="/master" element={<ProtectedRoute allowedPerfis={['ADMIN']}><ErrorBoundary><MasterLayout /></ErrorBoundary></ProtectedRoute>}>
                    <Route path="dashboard" element={<DashboardMaster />} />
                    <Route path="home" element={<DashboardBriefing />} />
                    <Route path="noc" element={<DashboardNOC />} />
                    <Route path="redes" element={<RedesPage />} />
                    <Route path="estados" element={<EstadosPage />} />
                    <Route path="municipios" element={<MunicipiosPage />} />
                    <Route path="escolas" element={<EscolasPage />} />
                    <Route path="escolas/:id" element={<EscolaDetalhePage />} />
                    <Route path="municipios/:id" element={<MunicipioDetalhePage />} />
                    <Route path="estados/:id" element={<EstadoDetalhePage />} />
                    <Route path="mapa" element={<MasterMapaPage />} />
                    <Route path="financeiro" element={<MasterFinanceiroPage />} />
                    <Route path="indices" element={<MasterIndicesPage />} />
                    <Route path="crescimento" element={<MasterCrescimentoPage />} />
                    <Route path="usuarios" element={<UsuariosPage />} />
                    <Route path="billing" element={<MasterBillingPage />} />
                    <Route path="planos" element={<MasterPlanosPage />} />
                    <Route path="assinaturas" element={<MasterAssinaturasPage />} />
                    <Route path="comunicados" element={<ComunicadosMasterPage />} />
                    <Route path="configuracoes" element={<MasterConfigPage />} />
                    <Route path="backups" element={<MasterBackupsPage />} />
                    <Route path="versao-codigo" element={<MasterVersionsPage />} />
                    <Route path="saude-operacional" element={<MasterHealthPage />} />
                    <Route path="login-acessos" element={<LoginAcessosPage />} />
                    <Route path="customizacao" element={<MasterCustomizacaoPage />} />
                    <Route path="perfil" element={<PerfilPage />} />
                    <Route path="alertas" element={<MeusAlertasPage />} />
                    {chatPremiumRoutes.map((route) => (
                      <Route key={route.path} path={route.path.replace('/master/', '')} element={route.element} />
                    ))}
                    <Route path="telas-descanso" element={<TvGradeManagementPage />} />
                    <Route path="ead" element={<EadCentroConhecimento perfil="administradores" />} />
                    <Route path="feedback" element={<MasterFeedbackPage />} />
                    <Route path="agentes-ia" element={<MasterAgentesIaPage />} />
                    <Route path="ecad-studio" element={<EcadStudioPageModular />} />
                    <Route path="ecad-studio-legacy" element={<EcadStudioPageModular />} />
                    <Route path="rfid" element={<MasterRfidDashboard />} />
                    <Route path="dispositivos" element={<MasterDispositivosPage />} />
                    <Route path="credenciais" element={<MasterCredenciaisPage />} />
                    <Route path="*" element={<ModulePlaceholder name="Módulo Master" />} />
                  </Route>

                  {/* ═══ SUPORTE (Chamados — usa MasterLayout) ═══ */}
                  <Route path="/suporte" element={<ProtectedRoute allowedPerfis={['ADMIN']}><ErrorBoundary><MasterLayout /></ErrorBoundary></ProtectedRoute>}>
                    <Route path="chamados" element={<SuporteChamadosPage />} />
                  </Route>

                  {/* ═══ COMERCIAL (Funil, Oportunidades, Contratos — usa MasterLayout) ═══ */}
                  <Route path="/comercial" element={<ProtectedRoute allowedPerfis={['ADMIN']}><ErrorBoundary><MasterLayout /></ErrorBoundary></ProtectedRoute>}>

                    <Route path="oportunidades" element={<OportunidadesPage />} />
                    <Route path="oportunidades/:id" element={<OportunidadeDetalhePage />} />
                    <Route path="contratos" element={<ContratosPage />} />
                  </Route>

                  {/* ═══ IMPLANTAÇÃO (Visitas, Treinamentos — usa MasterLayout) ═══ */}
                  <Route path="/implantacao" element={<ProtectedRoute allowedPerfis={['ADMIN']}><ErrorBoundary><MasterLayout /></ErrorBoundary></ProtectedRoute>}>
                    <Route index element={<ModuloEmConstrucao name="Implantações" description="Painel de acompanhamento de implantacoes em escolas, com checklist e cronograma." />} />
                    <Route path="visitas" element={<ModuloEmConstrucao name="Visitas Técnicas" description="Agendamento e registro de visitas tecnicas para instalacao e suporte." />} />
                    <Route path="treinamentos" element={<ModuloEmConstrucao name="Treinamentos" description="Gestao de treinamentos presenciais e remotos para equipes escolares." />} />
                  </Route>

                  {/* ═══ AGENDA (raiz — usa MasterLayout) ═══ */}
                  <Route path="/agenda" element={<ProtectedRoute allowedPerfis={['ADMIN']}><ErrorBoundary><MasterLayout /></ErrorBoundary></ProtectedRoute>}>
                    <Route index element={<ModuloEmConstrucao name="Agenda" description="Calendario de eventos, reunioes e compromissos da equipe." />} />
                  </Route>

                  {/* ═══ TAREFAS (raiz — usa MasterLayout) ═══ */}
                  <Route path="/tarefas" element={<ProtectedRoute allowedPerfis={['ADMIN']}><ErrorBoundary><MasterLayout /></ErrorBoundary></ProtectedRoute>}>
                    <Route index element={<ModuloEmConstrucao name="Tarefas" description="Gerenciador de tarefas da equipe com prioridades e acompanhamento." />} />
                  </Route>

                  {/* ═══ ADMIN / ESCOLA (Diretor) ═══ */}
                  <Route path="/admin" element={<ProtectedRoute allowedPerfis={['ADMIN', 'ESCOLA']}><ErrorBoundary><AdminLayout /></ErrorBoundary></ProtectedRoute>}>
                    <Route path="dashboard" element={<Dashboard />} />
                    <Route path="setup" element={<SetupPage />} />
                    <Route path="home" element={<DashboardBriefing />} />
                    <Route path="rede" element={<DashboardRede />} />
                    <Route path="estado" element={<DashboardEstado />} />
                    <Route path="municipio" element={<DashboardMunicipio />} />
                    <Route path="noc" element={<DashboardNOC />} />
                    <Route path="escola/:escolaId/dashboard" element={<DashboardEscolaConsolidado />} />
                    <Route path="escola/:escolaId/turma/:turmaId" element={<DashboardTurma />} />
                    <Route path="turnos" element={<TurnosPage />} />
                    <Route path="materias" element={<MateriasPage />} />
                    <Route path="equipe" element={<EquipePage />} />
                    <Route path="dispositivos" element={<DispositivosPage />} />
                    <Route path="turmas" element={<TurmasPage />} />
                    <Route path="turmas/:id" element={<TurmaDetalhePage />} />
                    <Route path="alunos" element={<AlunosPage />} />
                    <Route path="alunos/:id" element={<AlunoFichaPage />} />
                    <Route path="professores" element={<ProfessoresPage />} />
                    <Route path="professores/:id" element={<ProfessorDetalhePage />} />
                    <Route path="responsaveis" element={<ResponsaveisPage />} />
                    <Route path="chamada" element={<ChamadaPage />} />
                    <Route path="chamadas-gestao" element={<ChamadaGestaoPage />} />
                    <Route path="justificativas" element={<JustificativasGestao />} />
                    <Route path="cobertura" element={<CoberturaGradePage />} />
                    <Route path="comunicados" element={<ComunicadosEscolaPage />} />
                    <Route path="midias" element={<MidiasEscolaPage />} />
                    <Route path="mural" element={<ComunicadosMuralEscola />} />
                    <Route path="comunicados/mural" element={<ComunicadosMuralEscola />} />
                    <Route path="tv" element={<TvGradeEscola />} />
                    <Route path="comunicados/tv" element={<TvGradeEscola />} />
                    <Route path="estatisticas" element={<ComunicadosStatsEscola />} />
                    <Route path="comunicados/estatisticas" element={<ComunicadosStatsEscola />} />
                    <Route path="acesso" element={<AcessoPage />} />
                    <Route path="aprovacoes" element={<AprovacoesPage />} />
                    <Route path="gestao/redes" element={<RedesPage />} />
                    <Route path="gestao/estados" element={<EstadosPage />} />
                    <Route path="gestao/municipios" element={<MunicipiosPage />} />
                    <Route path="gestao/escolas" element={<EscolasPage />} />
                    <Route path="gestao/usuarios" element={<UsuariosPage />} />
                    <Route path="ativacoes" element={<AtivacoesPage />} />
                    <Route path="config/seguranca" element={<PainelSegurancaPage />} />
                    <Route path="perfil" element={<PerfilPage />} />
                    <Route path="alertas" element={<MeusAlertasPage />} />
                    <Route path="ead" element={<EadCentroConhecimento perfil="escola_admin" />} />
                    <Route path="chat" element={<ChatInterno />} />
                    <Route path="*" element={<ModulePlaceholder name="Módulo Admin" />} />
                  </Route>

                  {/* ═══ GESTOR MUNICIPAL ═══ */}
                  <Route path="/municipal" element={<ProtectedRoute allowedPerfis={['MUNICIPAL']}><ErrorBoundary><MunicipalLayout /></ErrorBoundary></ProtectedRoute>}>
                    <Route path="dashboard" element={<DashboardMunicipio />} />
                    <Route path="home" element={<DashboardBriefing />} />
                    <Route path="escolas" element={<EscolasMunicipal />} />
                    <Route path="comunicados" element={<ComunicadosMunicipal />} />
                    <Route path="midias" element={<MidiasMunicipal />} />
                    <Route path="mural" element={<ComunicadosMuralMunicipal />} />
                    <Route path="comunicados/mural" element={<ComunicadosMuralMunicipal />} />
                    <Route path="tv" element={<TvGradeMunicipal />} />
                    <Route path="comunicados/tv" element={<TvGradeMunicipal />} />
                    <Route path="estatisticas" element={<ComunicadosStatsMunicipal />} />
                    <Route path="comunicados/estatisticas" element={<ComunicadosStatsMunicipal />} />
                    <Route path="relatorios" element={<RelatoriosMunicipal />} />
                    <Route path="usuarios" element={<UsuariosMunicipal />} />
                    <Route path="perfil" element={<PerfilPage />} />
                    <Route path="alertas" element={<MeusAlertasPage />} />
                    <Route path="chat" element={<ChatInterno />} />
                    <Route path="configuracoes" element={<ConfiguracoesMunicipal />} />
                    <Route path="mapa" element={<MapaMunicipal />} />
                    <Route path="telas-descanso" element={<TvGradeMunicipal />} />
                    <Route path="ead" element={<EadCentroConhecimento perfil="municipal" />} />
                    <Route path="*" element={<ModulePlaceholder name="Módulo Municipal" />} />
                  </Route>

                  {/* ═══ GESTOR ESTADUAL ═══ */}
                  <Route path="/estadual" element={<ProtectedRoute allowedPerfis={['ESTADUAL']}><ErrorBoundary><EstadualLayout /></ErrorBoundary></ProtectedRoute>}>
                    <Route path="dashboard" element={<DashboardEstado />} />
                    <Route path="home" element={<DashboardBriefing />} />
                    <Route path="municipios" element={<MunicipiosPage />} />
                    <Route path="escolas" element={<EscolasPage />} />
                    <Route path="alunos" element={<AlunosPage />} />
                    <Route path="alunos/:id" element={<AlunoFichaPage />} />
                    <Route path="chamadas" element={<ChamadaGestaoPage />} />
                    <Route path="comunicados" element={<ComunicadosEscolaPage />} />
                    <Route path="acesso" element={<AcessoPage />} />
                    <Route path="usuarios" element={<UsuariosEstadual />} />
                    <Route path="perfil" element={<PerfilPage />} />
                    <Route path="alertas" element={<MeusAlertasPage />} />
                    <Route path="chat" element={<ChatInterno />} />
                    <Route path="configuracoes" element={<ConfiguracoesPage />} />
                    <Route path="telas-descanso" element={<TvGradeManagementPage />} />
                    <Route path="ead" element={<EadCentroConhecimento perfil="estadual" />} />
                    <Route path="*" element={<ModulePlaceholder name="Módulo Estadual" />} />
                  </Route>

                  {/* ═══ GESTOR DE REDE ═══ */}
                  <Route path="/rede" element={<ProtectedRoute allowedPerfis={['REDE']}><ErrorBoundary><RedeLayout /></ErrorBoundary></ProtectedRoute>}>
                    <Route path="dashboard" element={<DashboardRede />} />
                    <Route path="home" element={<DashboardBriefing />} />
                    <Route path="estados" element={<EstadosPage />} />
                    <Route path="municipios" element={<MunicipiosPage />} />
                    <Route path="escolas" element={<EscolasPage />} />
                    <Route path="comunicados" element={<ComunicadosEscolaPage />} />
                    <Route path="relatorios" element={<RelatoriosMunicipal />} />
                    <Route path="usuarios" element={<UsuariosRede />} />
                    <Route path="perfil" element={<PerfilPage />} />
                    <Route path="alertas" element={<MeusAlertasPage />} />
                    <Route path="chat" element={<ChatInterno />} />
                    <Route path="configuracoes" element={<ConfiguracoesPage />} />
                    <Route path="ead" element={<EadCentroConhecimento perfil="rede" />} />
                    <Route path="*" element={<ModulePlaceholder name="Módulo Rede" />} />
                  </Route>

                  {/* ═══ PROFESSOR ═══ */}
                  <Route path="/professor" element={<ProtectedRoute allowedPerfis={['PROFESSOR']}><ErrorBoundary><ProfessorLayout /></ErrorBoundary></ProtectedRoute>}>
                    <Route path="dashboard" element={<ProfessorDashboard />} />
                    <Route path="home" element={<DashboardBriefing />} />
                    <Route path="chamada" element={<ChamadaPage />} />
                    <Route path="diario" element={<DiarioClassePage />} />
                    <Route path="turmas" element={<MinhasTurmasPage />} />
                    <Route path="turmas/:id" element={<ProfessorTurmaRedirectPage />} />
                    <Route path="alunos" element={<ProfessorAlunosPage />} />
                    <Route path="alunos/:id" element={<ProfessorAlunoFichaPage />} />
                    <Route path="comunicados" element={<ProfessorComunicadosPage />} />
                    <Route path="avaliacoes" element={<AvaliacoesPage />} />
                    <Route path="avaliacoes/:id" element={<AvaliacaoDetalhePage />} />
                    <Route path="observacoes" element={<ObservacoesPage />} />
                    <Route path="relatorios" element={<RelatoriosProfessorPage />} />
                    <Route path="perfil" element={<PerfilPage />} />
                    <Route path="alertas" element={<MeusAlertasPage />} />
                    <Route path="configuracoes" element={<ProfessorConfiguracoesPage />} />
                    <Route path="chat" element={<ChatInterno />} />
                    <Route path="conversas-pais" element={<ChatDesktop />} />
                    <Route path="ead" element={<EadCentroConhecimento perfil="professor" />} />
                    <Route path="*" element={<ModulePlaceholder name="Módulo Professor" />} />
                  </Route>

                  {/* ═══ EQUIPE (Coordenação / Secretaria) ═══ */}
                  <Route path="/equipe" element={<ProtectedRoute allowedPerfis={['ESCOLA']}><ErrorBoundary><EquipeLayout /></ErrorBoundary></ProtectedRoute>}>
                    <Route path="dashboard" element={<EquipeDashboard />} />
                    <Route path="home" element={<DashboardBriefing />} />
                    <Route path="alunos" element={<AlunosPage />} />
                    <Route path="alunos/:id" element={<AlunoFichaPage />} />
                    <Route path="turmas" element={<TurmasPage />} />
                    <Route path="turmas/:id" element={<TurmaDetalhePage />} />
                    <Route path="turnos" element={<TurnosPage />} />
                    <Route path="materias" element={<MateriasPage />} />
                    <Route path="professores" element={<ProfessoresPage />} />
                    <Route path="professores/:id" element={<ProfessorDetalhePage />} />
                    <Route path="equipe" element={<EquipePage />} />
                    <Route path="frequencia" element={<ChamadaPage />} />
                    <Route path="chamadas-gestao" element={<ChamadaGestaoPage />} />
                    <Route path="justificativas" element={<JustificativasGestao />} />
                    <Route path="cobertura" element={<CoberturaGradePage />} />
                    <Route path="diario" element={<DiarioClassePage />} />
                    <Route path="portaria" element={<AcessoPage />} />
                    <Route path="incidentes" element={<IncidentesPage />} />
                    <Route path="comunicados" element={<ComunicadosEscolaPage />} />
                    <Route path="midias" element={<MidiasEscolaPage />} />
                    <Route path="responsaveis" element={<ResponsaveisPage />} />
                    <Route path="relatorios" element={<RelatoriosPage />} />
                    <Route path="perfil" element={<PerfilPage />} />
                    <Route path="alertas" element={<MeusAlertasPage />} />
                    <Route path="configuracoes" element={<ConfiguracoesPage />} />
                    <Route path="chat" element={<ChatInterno />} />
                    <Route path="conversas-pais" element={<ChatDesktop />} />
                    <Route path="ead" element={<EadCentroConhecimento perfil="escola_equipe" />} />
                    <Route path="*" element={<ModulePlaceholder name="Módulo Equipe" />} />
                  </Route>

                  {/* ═══ RESPONSÁVEL ═══ */}
                  <Route path="/responsavel" element={<ProtectedRoute allowedPerfis={['RESPONSAVEL']}><ErrorBoundary><ResponsavelLayout /></ErrorBoundary></ProtectedRoute>}>
                    <Route path="dashboard" element={<ResponsavelDashboard />} />
                    <Route path="home" element={<DashboardBriefing />} />
                    <Route path="filhos" element={<MeusFilhosPage />} />
                    <Route path="frequencia" element={<FrequenciaPage />} />
                    <Route path="comunicados" element={<ResponsavelComunicadosPage />} />
                    <Route path="notificacoes" element={<NotificacoesPage />} />
                    <Route path="justificativas" element={<JustificativasPage />} />
                    <Route path="desempenho" element={<DesempenhoPage />} />
                    <Route path="observacoes" element={<ResponsavelObservacoesPage />} />
                    <Route path="escola" element={<ResponsavelEscolaPage />} />
                    <Route path="perfil" element={<PerfilPage />} />
                    <Route path="configuracoes" element={<ConfiguracoesPage />} />
                    <Route path="agenda" element={<AgendaConsolidada />} />
                    <Route path="ead" element={<EadCentroConhecimento perfil="responsavel" />} />
                    <Route path="*" element={<ModulePlaceholder name="Módulo Responsável" />} />
                  </Route>

                  {/* ═══ ALUNO ═══ */}
                  <Route path="/aluno" element={<ProtectedRoute allowedPerfis={['ALUNO']}><ErrorBoundary><AlunoLayout /></ErrorBoundary></ProtectedRoute>}>
                    <Route path="dashboard" element={<AlunoDashboard />} />
                    <Route path="grade" element={<MinhaGradePage />} />
                    <Route path="frequencia" element={<FrequenciaAlunoPage />} />
                    <Route path="comunicados" element={<ComunicadosPage />} />
                    <Route path="perfil" element={<PerfilPage />} />
                    <Route path="alertas" element={<MeusAlertasPage />} />
                    <Route path="configuracoes" element={<ConfiguracoesPage />} />
                    <Route path="agenda" element={<AgendaConsolidada />} />
                    <Route path="ead" element={<EadCentroConhecimento perfil="aluno" />} />
                    <Route path="*" element={<ModulePlaceholder name="Módulo Aluno" />} />
                  </Route>

                  {/* ═══ PÚBLICO / LEAD ═══ */}
                  <Route path="/public/dashboard" element={<ProtectedRoute allowedPerfis={['PUBLICO']}><PublicDashboardPage /></ProtectedRoute>} />

                  {/* ═══ MOBILE PWA ═══ */}
                  <Route path="/mobile/login" element={<Navigate to="/login" replace />} />
                  <Route path="/mobile" element={<MobileLayout />}>
                    <Route index element={<MobileHome />} />
                    <Route path="filhos" element={<FilhosTimeline />} />
                    <Route path="grade" element={<AlunoGrade />} />
                    <Route path="comunicados" element={<MobileComunicados />} />
                    <Route path="perfil" element={<MobilePerfil />} />
                    <Route path="conversas" element={<Conversas />} />
                    <Route path="conversas/:id" element={<ConversaDetalhe />} />
                  </Route>

                  <Route path="*" element={<Navigate to="/admin/dashboard" replace />} />
                </Routes>
                {(import.meta.env.DEV || import.meta.env.VITE_LAB_MODE === '1') && <LabExecutionOverlay />}
                </InAppNotificationWatcher>
              </PwaBiometricLockProvider>
            </InactivityProvider>
            </LabContextProvider>
          </DevStatusGate>
          <PwaUpdateWatcher />
        </BrowserRouter>
      </AuthProvider>
      <Toaster richColors position="top-right" />
      <GlobalF4Handler />
    </QueryClientProvider>
  );
}

export default App;
